import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaUserAlt, FaKey, FaLock, FaEye, FaEyeSlash, FaArrowLeft, FaCheckCircle } from "react-icons/fa";
import templeIcon from "../../assets/images/BELL.gif";
import { forgotPassword, resetPassword } from "../../services/auth.service";
import Swal from "sweetalert2";
import "./login.css";

const ForgotPasswordComponent = () => {
    const navigate = useNavigate();

    // Step 1: Request OTP, Step 2: Verify OTP & Reset Password
    const [step, setStep] = useState(1);
    const [identifier, setIdentifier] = useState("");
    const [maskedEmail, setMaskedEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [countdown, setCountdown] = useState(0);

    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const showNotification = (text, type = "danger") => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: "", type: "" }), 5000);
    };

    // Step 1: Submit email / mobile to get OTP
    const handleRequestOtp = async (values) => {
        setMessage({ text: "", type: "" });
        setLoading(true);
        try {
            const trimmed = values.identifier.trim();
            const response = await forgotPassword(trimmed);

            if (response.success) {
                setIdentifier(trimmed);
                setMaskedEmail(response.data?.maskedEmail || "");
                setStep(2);
                setCountdown(60); // 60s cooldown for resend
                showNotification(response.message || "OTP sent successfully to your registered email.", "success");
            } else {
                showNotification(response.message || "Failed to initiate password reset.", "danger");
            }
        } catch (error) {
            console.error("Forgot password request error:", error);
            const errText = error.response?.data?.message || "Something went wrong. Please check your details and try again.";
            showNotification(errText, "danger");
        } finally {
            setLoading(false);
        }
    };

    // Resend OTP in Step 2
    const handleResendOtp = async () => {
        if (countdown > 0 || !identifier) return;
        setLoading(true);
        try {
            const response = await forgotPassword(identifier);
            if (response.success) {
                setCountdown(60);
                showNotification("A new OTP has been sent to your registered email.", "success");
            } else {
                showNotification(response.message || "Could not resend OTP.", "danger");
            }
        } catch (error) {
            const errText = error.response?.data?.message || "Failed to resend OTP.";
            showNotification(errText, "danger");
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Submit OTP & new passwords
    const handleResetPassword = async (values) => {
        setMessage({ text: "", type: "" });
        setLoading(true);
        try {
            const response = await resetPassword(
                identifier,
                values.otp.trim(),
                values.newPassword,
                values.confirmPassword
            );

            if (response.success) {
                Swal.fire({
                    title: "Password Reset Successfully!",
                    text: response.message || "Your password has been updated. Please sign in with your new password.",
                    icon: "success",
                    confirmButtonColor: "#2b53a3",
                }).then(() => {
                    navigate("/login");
                });
            } else {
                showNotification(response.message || "Password reset failed.", "danger");
            }
        } catch (error) {
            console.error("Reset password error:", error);
            const errText = error.response?.data?.message || "Failed to reset password. Please check your OTP and try again.";
            showNotification(errText, "danger");
        } finally {
            setLoading(false);
        }
    };

    const step1ValidationSchema = Yup.object().shape({
        identifier: Yup.string()
            .trim()
            .required("Please enter your registered mobile number or email address")
            .min(3, "Must be at least 3 characters"),
    });

    const step2ValidationSchema = Yup.object().shape({
        otp: Yup.string()
            .trim()
            .required("OTP is required")
            .matches(/^[0-9]{6}$/, "OTP must be exactly 6 digits"),
        newPassword: Yup.string()
            .required("New password is required")
            .min(8, "Password must be at least 8 characters")
            .matches(/[A-Z]/, "Must include at least one uppercase letter")
            .matches(/[a-z]/, "Must include at least one lowercase letter")
            .matches(/[0-9]/, "Must include at least one number")
            .matches(/[@$!%*?&]/, "Must include at least one special character"),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("newPassword"), null], "Passwords do not match")
            .required("Confirm password is required"),
    });

    return (
        <div className="login-body">
            <div className="container d-flex align-items-center justify-content-center min-vh-100 py-4">
                <div className="login-wrapper p-4 rounded-4 shadow-lg bg-white text-dark" style={{ maxWidth: "480px", width: "100%" }}>
                    <div className="text-center mb-3">
                        <img src={templeIcon} alt="Temple" className="temple-icon mb-2" />
                        <h4 className="fw-bold temple-text">Forgot Password</h4>
                        <p className="text-muted small">
                            {step === 1
                                ? "Enter your registered Mobile Number or Email to receive an OTP."
                                : "Enter the OTP sent to your email to set a new password."}
                        </p>
                    </div>

                    {message.text && (
                        <div className={`alert alert-${message.type} text-center py-2 px-3 small`} role="alert">
                            {message.text}
                        </div>
                    )}

                    {step === 1 ? (
                        /* STEP 1: Enter Identifier */
                        <Formik
                            initialValues={{ identifier: "" }}
                            validationSchema={step1ValidationSchema}
                            onSubmit={handleRequestOtp}
                        >
                            {() => (
                                <Form className="login-form">
                                    <div className="mb-3 custom-label">
                                        <label className="signup-label">Mobile Number or Email</label>
                                        <div className="signup-input-box">
                                            <FaUserAlt className="signup-icon" />
                                            <Field
                                                type="text"
                                                name="identifier"
                                                className="signup-input"
                                                placeholder="Enter 10-digit mobile or email"
                                                autoFocus
                                            />
                                        </div>
                                        <ErrorMessage name="identifier" component="div" className="text-danger small mt-1" />
                                    </div>

                                    <div className="d-grid mt-4">
                                        <button
                                            type="submit"
                                            className="btn btn-warning btn-lg rounded-pill fw-medium"
                                            disabled={loading}
                                        >
                                            {loading && <span className="spinner-border spinner-border-sm me-2"></span>}
                                            Send Verification OTP
                                        </button>
                                    </div>

                                    <div className="text-center mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 text-decoration-none fw-medium text-secondary small"
                                            onClick={() => navigate("/login")}
                                        >
                                            <FaArrowLeft className="me-1" /> Back to Sign In
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    ) : (
                        /* STEP 2: Enter OTP & New Password */
                        <div>
                            {maskedEmail && (
                                <div className="p-2 mb-3 bg-light rounded text-center small border">
                                    OTP dispatched to: <strong className="text-primary">{maskedEmail}</strong>
                                </div>
                            )}

                            <Formik
                                initialValues={{
                                    otp: "",
                                    newPassword: "",
                                    confirmPassword: "",
                                }}
                                validationSchema={step2ValidationSchema}
                                onSubmit={handleResetPassword}
                            >
                                {() => (
                                    <Form className="login-form">
                                        {/* OTP Input */}
                                        <div className="mb-3 custom-label">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <label className="signup-label m-0">6-Digit OTP</label>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 text-decoration-none small text-primary"
                                                    onClick={handleResendOtp}
                                                    disabled={countdown > 0 || loading}
                                                >
                                                    {countdown > 0 ? `Resend OTP (${countdown}s)` : "Resend OTP"}
                                                </button>
                                            </div>
                                            <div className="signup-input-box">
                                                <FaKey className="signup-icon" />
                                                <Field
                                                    type="text"
                                                    name="otp"
                                                    maxLength="6"
                                                    className="signup-input"
                                                    placeholder="Enter 6-digit OTP"
                                                    autoFocus
                                                />
                                            </div>
                                            <ErrorMessage name="otp" component="div" className="text-danger small mt-1" />
                                        </div>

                                        {/* New Password */}
                                        <div className="mb-3 custom-label">
                                            <label className="signup-label">New Password</label>
                                            <div className="signup-input-box">
                                                <FaLock className="signup-icon" />
                                                <Field
                                                    type={showNewPassword ? "text" : "password"}
                                                    name="newPassword"
                                                    className="signup-input"
                                                    placeholder="Minimum 8 characters"
                                                />
                                                <span
                                                    className="signup-eye"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                >
                                                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                                </span>
                                            </div>
                                            <ErrorMessage name="newPassword" component="div" className="text-danger small mt-1" />
                                        </div>

                                        {/* Confirm Password */}
                                        <div className="mb-3 custom-label">
                                            <label className="signup-label">Confirm Password</label>
                                            <div className="signup-input-box">
                                                <FaLock className="signup-icon" />
                                                <Field
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    name="confirmPassword"
                                                    className="signup-input"
                                                    placeholder="Re-enter new password"
                                                />
                                                <span
                                                    className="signup-eye"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                >
                                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                                </span>
                                            </div>
                                            <ErrorMessage name="confirmPassword" component="div" className="text-danger small mt-1" />
                                        </div>

                                        <div className="d-grid mt-4">
                                            <button
                                                type="submit"
                                                className="btn btn-warning btn-lg rounded-pill fw-medium"
                                                disabled={loading}
                                            >
                                                {loading && <span className="spinner-border spinner-border-sm me-2"></span>}
                                                <FaCheckCircle className="me-1" /> Reset Password
                                            </button>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 text-decoration-none small text-secondary"
                                                onClick={() => {
                                                    setStep(1);
                                                    setMessage({ text: "", type: "" });
                                                }}
                                            >
                                                <FaArrowLeft className="me-1" /> Change Email/Mobile
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 text-decoration-none small text-secondary"
                                                onClick={() => navigate("/login")}
                                            >
                                                Back to Sign In
                                            </button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    )}

                    <p className="text-center mt-4 text-muted small mb-0">© {new Date().getFullYear()} TMS</p>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordComponent;

